/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isMarathonAgent } from '@atlassian/conversation-assistant-service/common/utils/is-marathon-agent';
import { UncontrolledExpander } from '@atlassian/conversation-assistant-ui-components/ui/UncontrolledExpander';
import { type TraceInteractionEvent } from '@atlassian/rovo-platform-ui-components/compact-research-steps';
import {
	type ExpandableTrace,
	MessageLoading,
} from '@atlassian/rovo-platform-ui-components/loading-state/MessageLoading';

import { ResearchNotificationPanel } from '../../../common/ui/research-notification-panel';
import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { type ResearchStep } from '../../../controllers/chat-message-research-store/MessageResearch';
import { useChatMessageResearchById } from '../../../controllers/chat-message-research-store/useChatMessageResearchById';
import { useConversation } from '../../../controllers/conversation/useConversation';

import messages from './messages';

const useResearchStepsAsExpandableTrace = ({
	researchSteps,
}: {
	researchSteps: ResearchStep[];
}): ExpandableTrace[] => {
	const { customHandlers } = useUIConfig();
	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { conversation } = useConversation();
	const { formatMessage } = useIntl();
	return useMemo(
		() =>
			researchSteps.map((step) => {
				const content = step.content && (
					<ContentRenderer
						content={step.content}
						smallerFont={true}
						productKey={productKey}
						siteId={siteId}
						aaid={aaid}
						conversationId={conversation?.id}
						onDeepLinkClick={customHandlers?.handleDeepLinkClick}
						shouldOverrideCardAsInline={true}
					/>
				);

				return {
					title: step.title ?? formatMessage(messages.researchStepTitlePlaceholder),
					status: step.completed ? 'success' : 'loading',
					defaultExpanded: false,
					content,
				};
			}),
		[
			researchSteps,
			productKey,
			siteId,
			aaid,
			conversation?.id,
			formatMessage,
			customHandlers?.handleDeepLinkClick,
		],
	);
};

const ResearchTraces = ({
	expandableTraces,
	researchCompleted,
	hasReceivedAnswerPart,
	hideLiveThinkingIndicator,
	agent,
	generatingText,
	personalityThinkingText,
	onTraceInteraction,
}: {
	expandableTraces: ExpandableTrace[];
	researchCompleted: boolean;
	hasReceivedAnswerPart?: boolean;
	hideLiveThinkingIndicator?: boolean;
	agent?: Agent;
	generatingText?: string;
	personalityThinkingText?: string;
	answerNowSlot?: React.ReactNode;
	onTraceInteraction?: (event: TraceInteractionEvent) => void;
}) => {
	const { formatMessage } = useIntl();

	let thinkingText = personalityThinkingText ?? formatMessage(messages.rovoIsThinking);

	if (agent) {
		thinkingText = formatMessage(messages.agentIsThinking, { agentName: agent.name });
	}

	if (generatingText) {
		thinkingText = generatingText;
	}

	return (
		<Box testId="research-step-list">
			<MessageLoading
				hideThinkingIndicator={
					hideLiveThinkingIndicator || hasReceivedAnswerPart || researchCompleted
				}
				traces={expandableTraces}
				thinkingText={thinkingText}
				agent={agent}
				animation={true}
				onTraceInteraction={onTraceInteraction}
			/>
		</Box>
	);
};

type MessageDeepResearchProgressProps = {
	researchCompleted: boolean;
	researchErrored?: boolean;
	researchSteps: ResearchStep[];
	hasReceivedAnswerPart?: boolean;
	hideLiveThinkingIndicator?: boolean;
	agent?: Agent;
	generatingText?: string;
	personalityThinkingText?: string;
	onTraceInteraction?: (event: TraceInteractionEvent) => void;
};

const MessageDeepResearchProgress = ({
	researchCompleted,
	researchErrored,
	researchSteps,
	hasReceivedAnswerPart,
	hideLiveThinkingIndicator,
	agent,
	generatingText,
	personalityThinkingText,
	onTraceInteraction,
}: MessageDeepResearchProgressProps) => {
	const { formatMessage } = useIntl();
	const [panelExpanded, setPanelExpanded] = useState(false);
	const expandableTraces = useResearchStepsAsExpandableTrace({ researchSteps });

	// Marathon agents should never see the "Deep research can take a few
	// minutes..." banner because they aren't doing deep research.
	const { selectedAgent } = useConversation();
	const hideResearchNotification = isMarathonAgent(selectedAgent);

	return (
		<Stack space="space.200">
			{!hideResearchNotification && (
				<ResearchNotificationPanel
					researchCompleted={researchCompleted}
					researchErrored={researchErrored}
					hasReceivedAnswerPart={hasReceivedAnswerPart}
				/>
			)}
			{researchCompleted || hasReceivedAnswerPart ? (
				<UncontrolledExpander
					isExpanded={panelExpanded}
					showText={formatMessage(messages.steps, { count: researchSteps.length })}
					onClick={() => setPanelExpanded(!panelExpanded)}
				>
					<ResearchTraces
						expandableTraces={expandableTraces}
						researchCompleted={researchCompleted}
						hasReceivedAnswerPart={hasReceivedAnswerPart}
						hideLiveThinkingIndicator={hideLiveThinkingIndicator}
						agent={agent}
						generatingText={generatingText}
						personalityThinkingText={personalityThinkingText}
						onTraceInteraction={onTraceInteraction}
					/>
				</UncontrolledExpander>
			) : (
				<ResearchTraces
					expandableTraces={expandableTraces}
					researchCompleted={researchCompleted}
					hasReceivedAnswerPart={hasReceivedAnswerPart}
					hideLiveThinkingIndicator={hideLiveThinkingIndicator}
					agent={agent}
					generatingText={generatingText}
					personalityThinkingText={personalityThinkingText}
					onTraceInteraction={onTraceInteraction}
				/>
			)}
		</Stack>
	);
};

export const MessageDeepResearchV2Refresh = ({
	messageId,
	agent,
	generatingText,
	personalityThinkingText,
	hideLiveThinkingIndicator,
}: {
	messageId: string;
	agent?: Agent;
	generatingText?: string;
	personalityThinkingText?: string;
	hideLiveThinkingIndicator?: boolean;
	answerNowSlot?: React.ReactNode;
}): JSX.Element | undefined => {
	const {
		artificiallyProgressingSteps,
		errored,
		currentProgress,
		researchCompleted,
		hasReceivedAnswerPart,
	} = useChatMessageResearchById({
		messageId,
	});
	const { sendAnalyticsEvent } = useAnalytics();

	const hasVisibleArtificialResearchSteps =
		!errored && (artificiallyProgressingSteps?.length > 0 || !!currentProgress);

	if (!hasVisibleArtificialResearchSteps) {
		return;
	}

	return (
		<MessageDeepResearchProgress
			researchCompleted={!!researchCompleted}
			researchErrored={errored}
			researchSteps={artificiallyProgressingSteps}
			hasReceivedAnswerPart={hasReceivedAnswerPart}
			hideLiveThinkingIndicator={hideLiveThinkingIndicator}
			agent={agent}
			generatingText={generatingText}
			personalityThinkingText={personalityThinkingText}
			onTraceInteraction={({ type, expanded }) => {
				sendAnalyticsEvent({
					eventType: 'ui',
					action: expanded ? 'expanded' : 'collapsed',
					actionSubject: 'trace',
					actionSubjectId: type,
					attributes: {
						messageId,
					},
				});
			}}
		/>
	);
};
